import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { ChapterState } from '@/types';

import { ChapterTileIcon } from './ChapterTileIcon';
import { EpisodeChapterStatus } from './EpisodeChapterStatus';

export type EpisodeChapterTileComponentProps = {
  index: number;
  onIconClick: (e: React.MouseEvent) => void;
  onTileClick: () => void;
  chapterType: string;
  chapterName: string;
  chapterState: ChapterState;
  chapterRemainingDuration: number;
  chapterProgressPercentage: number;
  isEpisodeRedeemRequired: boolean;
  isChapterActive: boolean;
  isChapterVerifiable: boolean;
  isPlayerActive: boolean;
};

const PROGRESS_BAR_HEIGHT = 6;

export const EpisodeChapterTile = ({
  index,
  onIconClick,
  onTileClick,
  chapterType,
  chapterName,
  chapterState,
  chapterRemainingDuration,
  chapterProgressPercentage,
  isEpisodeRedeemRequired,
  isChapterActive,
  isChapterVerifiable,
  isPlayerActive,
}: EpisodeChapterTileComponentProps) => {
  const iconRef = useRef<HTMLButtonElement>(null);
  const [isIconHovered, setIsIconHovered] = useState(false);
  const { palette } = useTheme();

  const tileBackground = isChapterActive
    ? palette.chapter.active
    : palette.chapter.main;
  const darknessLevel = isChapterActive ? 0.1 : 0.05;
  const hoverTileBackground = `linear-gradient(0deg, rgba(0, 0, 0, ${darknessLevel}) 0%, rgba(0, 0, 0, ${darknessLevel}) 100%), ${tileBackground}`;

  useEffect(() => {
    const handleMouseOver = () => setIsIconHovered(true);
    const handleMouseOut = () => setIsIconHovered(false);

    const node = iconRef.current;
    if (node) {
      node.addEventListener('mouseover', handleMouseOver);
      node.addEventListener('mouseout', handleMouseOut);
      return () => {
        node.removeEventListener('mouseover', handleMouseOver);
        node.removeEventListener('mouseout', handleMouseOut);
      };
    }
  }, [iconRef]);

  return (
    <Box
      onClick={onTileClick}
      sx={{
        ...styles.chapterTile,
        bgcolor: tileBackground,
        '&:hover, &:focus': {
          background: hoverTileBackground,
        },
      }}>
      <Box sx={styles.chapterDetails}>
        <Box sx={styles.titleContainer}>
          <Typography
            variant="body2SemiBold"
            sx={{ color: 'text.main', mr: 1 }}>
            {index + 1}.
          </Typography>
          <Box>
            <Typography
              variant="body2SemiBold"
              sx={{ color: 'text.main', mb: 1 }}>
              {chapterName}
            </Typography>
            <EpisodeChapterStatus
              isChapterActive={isChapterActive}
              chapterState={chapterState}
              chapterRemainingDuration={chapterRemainingDuration}
              isChapterVerifiable={isChapterVerifiable}
            />
          </Box>
        </Box>

        <IconButton
          ref={iconRef}
          onClick={onIconClick}
          aria-label={isChapterActive && isPlayerActive ? 'Pause' : 'Play'}>
          <Box sx={styles.iconContainer}>
            <ChapterTileIcon
              index={index}
              chapterState={chapterState}
              chapterType={chapterType}
              isChapterPlaying={isChapterActive && isPlayerActive}
              isChapterVerifiable={isChapterVerifiable}
              isEpisodeRedeemRequired={isEpisodeRedeemRequired}
              isIconHovered={isIconHovered}
            />
          </Box>
        </IconButton>
      </Box>
      <Box sx={styles.progressBarContainer}>
        <Box
          sx={styles.progressBar}
          width={`${chapterProgressPercentage}%`}
          height={PROGRESS_BAR_HEIGHT}
        />
      </Box>
    </Box>
  );
};

const styles = {
  chapterTile: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    mb: 1,
    borderRadius: '16px',
    cursor: 'pointer',
  },
  chapterDetails: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: 2,
  },
  titleContainer: {
    display: 'flex',
  },
  progressBarContainer: {
    position: 'relative',
    width: 'calc(100% + 8px)',
    textAlign: 'center',
  },
  progressBar: {
    position: 'absolute',
    backgroundColor: 'secondary.active',
    right: -10,
    bottom: 0,
    left: 0,
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: 'white',
    boxShadow: '0px 0.762px 3.048px 0px rgba(0, 0, 0, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
};
